import * as React from "react";
import type { StateData, UserType, MapColorScale } from '../../types/geographic';

interface GoogleMapDataLayerProps {
  data: StateData[];
  userType: UserType;
  colorScale: MapColorScale;
  onStateClick?: (state: StateData) => void;
  onStateHover?: (state: StateData | null) => void;
  isLoading?: boolean;
  className?: string;
  googleMapsApiKey?: string;
}

/**
 * Google Maps with Data Layer Implementation
 * Uses Google Maps Data Layer API for accurate US state boundaries
 */
const GoogleMapDataLayer: React.FC<GoogleMapDataLayerProps> = ({
  data,
  userType,
  colorScale,
  onStateClick,
  onStateHover,
  isLoading = false,
  className = "",
  googleMapsApiKey
}) => {
  const mapContainerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<google.maps.Map | null>(null);
  const infoWindowRef = React.useRef<google.maps.InfoWindow | null>(null);
  const [mapLoaded, setMapLoaded] = React.useState(false);

  // Get API key from meta tag if not provided
  const apiKey = React.useMemo(() => {
    if (googleMapsApiKey) {
      return googleMapsApiKey;
    }
    
    const metaTag = document.querySelector('meta[name="google-maps-api-key"]');
    if (metaTag) {
      const key = metaTag.getAttribute('content') || '';
      return key;
    }
    
    console.error('[GoogleMapDataLayer] No Google Maps API key found!');
    return '';
  }, [googleMapsApiKey]);

  // Helper function to get user value
  const getUserValue = React.useCallback((state: StateData): number => {
    switch (userType) {
      case 'clients':
        return state.client_count;
      case 'professionals':
        return state.professional_count;
      case 'all':
      default:
        return state.user_count;
    }
  }, [userType]);

  // Helper function to get color for value
  const getColorForValue = React.useCallback((value: number): string => {
    if (value === 0) return colorScale.colors[0];
    
    for (let i = 0; i < colorScale.breaks.length - 1; i++) {
      if (value >= colorScale.breaks[i] && value < colorScale.breaks[i + 1]) {
        return colorScale.colors[i] || colorScale.colors[colorScale.colors.length - 1];
      }
    }
    
    return colorScale.colors[colorScale.colors.length - 1];
  }, [colorScale]);

  // Initialize Google Maps
  React.useEffect(() => {
    if (!apiKey) {
      return;
    }

    let scriptLoadTimeout: NodeJS.Timeout;
    let initTimer: NodeJS.Timeout;

    const initializeMap = () => {
      if (!mapContainerRef.current) return;
      
      // Create map instance
      const map = new google.maps.Map(mapContainerRef.current, {
        zoom: 4,
        center: { lat: 39.8283, lng: -98.5795 }, // Geographic center of US
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        zoomControl: true,
        styles: [
          {
            featureType: "administrative.province",
            elementType: "geometry.stroke",
            stylers: [{ visibility: "on" }, { weight: 1 }]
          },
          {
            featureType: "poi",
            stylers: [{ visibility: "off" }]
          },
          {
            featureType: "road",
            elementType: "labels.icon",
            stylers: [{ visibility: "off" }]
          },
          {
            featureType: "transit",
            stylers: [{ visibility: "off" }]
          }
        ]
      });

      mapRef.current = map;

      // Create info window
      const infoWindow = new google.maps.InfoWindow();
      infoWindowRef.current = infoWindow;

      // Load US state boundaries using Google's Data Layer
      // This URL provides official US state boundaries in GeoJSON format
      map.data.loadGeoJson('https://storage.googleapis.com/mapsdevsite/json/states.js');

      // Add error handling for data layer
      map.data.addListener('error', (error: any) => {
        console.error('[GoogleMapDataLayer] Data layer error:', error);
      });

      setMapLoaded(true);
    };

    const loadGoogleMaps = () => {
      // Check if already loaded
      if (window.google && window.google.maps) {
        initializeMap();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=visualization`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        clearTimeout(scriptLoadTimeout);
        initializeMap();
      };
      
      script.onerror = (error) => {
        console.error('[GoogleMapDataLayer] Failed to load Google Maps:', error);
        clearTimeout(scriptLoadTimeout);
      };

      document.head.appendChild(script);

      // Timeout fallback
      scriptLoadTimeout = setTimeout(() => {
        if (!window.google || !window.google.maps) {
          console.error('[GoogleMapDataLayer] Google Maps failed to load within timeout');
        }
      }, 10000);
    };

    // Small delay to ensure DOM is ready
    initTimer = setTimeout(() => {
      if (!mapContainerRef.current) {
        console.error('[GoogleMapDataLayer] Map container ref is still null after delay');
        return;
      }

      loadGoogleMaps();
    }, 100);

    return () => {
      if (scriptLoadTimeout) {
        clearTimeout(scriptLoadTimeout);
      }
      if (initTimer) {
        clearTimeout(initTimer);
      }
    };
  }, [apiKey]);

  // Apply data styling when map is loaded and data changes
  React.useEffect(() => {
    if (!mapRef.current || !mapLoaded || !data) {
      return;
    }
    
    // Clear any existing listeners to prevent duplicates
    google.maps.event.clearListeners(mapRef.current.data, 'click');
    google.maps.event.clearListeners(mapRef.current.data, 'mouseover');
    google.maps.event.clearListeners(mapRef.current.data, 'mouseout');

    // Create state data lookup map with multiple matching strategies
    const stateDataMap = new Map<string, StateData>();
    data.forEach(state => {
      // Key by full state name (uppercase for case-insensitive matching)
      const stateName = state.state_name?.toUpperCase();
      if (stateName) {
        stateDataMap.set(stateName, state);
      }
      
      // Also key by state_code if it differs from state_name
      const stateCode = state.state_code?.toUpperCase();
      if (stateCode && stateCode !== stateName) {
        stateDataMap.set(stateCode, state);
      }
    });
    
    // Style the data layer based on our data
    mapRef.current.data.setStyle((feature) => {
      // Get state name from the feature - this is the full state name like "California"
      const googleStateName = feature.getProperty('NAME');
      
      // Try to find matching data - our data uses full state names, so match by NAME property
      let stateData = stateDataMap.get(googleStateName?.toUpperCase());

      if (stateData) {
        const userValue = getUserValue(stateData);
        const fillColor = getColorForValue(userValue);
        const fillOpacity = userValue > 0 ? 0.7 : 0.3;

        return {
          fillColor: fillColor,
          fillOpacity: fillOpacity,
          strokeColor: '#ffffff',
          strokeWeight: 1,
          strokeOpacity: 0.8
        };
      }

      // Default style for states without data
      return {
        fillColor: '#e0e0e0',
        fillOpacity: 0.3,
        strokeColor: '#ffffff',
        strokeWeight: 1,
        strokeOpacity: 0.8
      };
    });

    // Add click listener
    mapRef.current.data.addListener('click', (event: any) => {
      const googleStateName = event.feature.getProperty('NAME');
      const stateData = stateDataMap.get(googleStateName?.toUpperCase());
      
      if (stateData && onStateClick) {
        onStateClick(stateData);
      }
    });

    // Add mouseover listener
    mapRef.current.data.addListener('mouseover', (event: any) => {
      const googleStateName = event.feature.getProperty('NAME');
      const stateData = stateDataMap.get(googleStateName?.toUpperCase());
      
      if (stateData && infoWindowRef.current && mapRef.current) {
        // Highlight the state
        mapRef.current.data.overrideStyle(event.feature, {
          fillOpacity: 0.9,
          strokeWeight: 2
        });

        // Show info window
        const currentUserValue = getUserValue(stateData);
        const currentUserLabel = userType === 'clients' ? 'Clients' : 
                               userType === 'professionals' ? 'Professionals' : 'Total Users';
        
        const content = `
          <div style="min-width: 200px; padding: 8px;">
            <h6 style="margin: 0 0 8px 0; font-weight: bold;">${stateData.state_name}</h6>
            <div style="font-size: 14px;">
              <div style="margin-bottom: 6px; padding: 4px 8px; background-color: #e3f2fd; border-radius: 4px; border-left: 3px solid #2196f3;">
                <strong>${currentUserLabel}:</strong> <span style="font-size: 16px; color: #1976d2;">${currentUserValue.toLocaleString()}</span>
              </div>
              <div style="margin-bottom: 4px;">
                <strong>Total Users:</strong> ${stateData.user_count.toLocaleString()}
              </div>
              <div style="margin-bottom: 4px;">
                <strong>Clients:</strong> ${stateData.client_count.toLocaleString()}
              </div>
              <div style="margin-bottom: 4px;">
                <strong>Professionals:</strong> ${stateData.professional_count.toLocaleString()}
              </div>
              ${stateData.revenue && stateData.revenue > 0 ? `
                <div style="margin-bottom: 4px;">
                  <strong>Revenue:</strong> $${stateData.revenue.toLocaleString('en-US', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })}
                </div>
              ` : ''}
              ${stateData.roi ? `
                <div>
                  <strong>ROI:</strong> ${stateData.roi.toFixed(1)}%
                </div>
              ` : ''}
            </div>
          </div>
        `;
        
        infoWindowRef.current.setContent(content);
        
        // Data Layer events use event.latLng for mouse position
        if (event.latLng) {
          infoWindowRef.current.setPosition(event.latLng);
          infoWindowRef.current.open(mapRef.current);
        } else {
          console.warn('[GoogleMapDataLayer] No latLng in event, InfoWindow not shown');
        }

        if (onStateHover) {
          onStateHover(stateData);
        }
      }
    });

    // Add mouseout listener
    mapRef.current.data.addListener('mouseout', () => {
      mapRef.current?.data.revertStyle();
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
      if (onStateHover) {
        onStateHover(null);
      }
    });
  }, [mapLoaded, data, userType, colorScale, getUserValue, getColorForValue, onStateClick, onStateHover]);

  if (!apiKey) {
    return (
      <div className={`google-map-data-layer ${className}`.trim()}>
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3" aria-hidden="true"></i>
            <h5 className="text-warning">Google Maps API Key Required</h5>
            <p className="text-muted mb-3">
              Google Maps API key is required to display the map visualization.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`google-map-data-layer ${className}`.trim()}>
        <div className="card">
          <div className="card-body text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading map...</span>
            </div>
            <p className="text-muted mb-0">Loading Google Maps...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`google-map-data-layer ${className}`.trim()}>
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="fas fa-map me-2" aria-hidden="true"></i>
              US States - {userType === 'clients' ? 'Client' : 
                          userType === 'professionals' ? 'Professional' : 'User'} Distribution
            </h5>
            <div className="d-flex gap-2 align-items-center">
              <small className="text-muted">
                <i className="fab fa-google me-1" aria-hidden="true"></i>
                Powered by Google Maps
              </small>
              <small className="text-muted">
                States with data: {data.length}/50
              </small>
            </div>
          </div>
        </div>
        <div className="card-body p-0 position-relative">
          <div ref={mapContainerRef} style={{ height: '500px', width: '100%' }} />
          
          {/* Legend */}
          <div className="position-absolute bg-white border rounded shadow-sm p-3" style={{
            top: '10px',
            right: '10px',
            zIndex: 1,
            maxWidth: '200px'
          }}>
            <h6 className="mb-3 fw-semibold">
              {userType === 'clients' ? 'Client' : 
               userType === 'professionals' ? 'Professional' : 'User'} Count
            </h6>
            <div className="d-flex flex-column gap-2">
              {colorScale.breaks.slice(0, -1).map((breakPoint, index) => {
                const nextBreak = colorScale.breaks[index + 1];
                const color = colorScale.colors[index];
                const label = index === 0 ? `0` : 
                             index === colorScale.breaks.length - 2 ? `${Math.round(breakPoint)}+` :
                             `${Math.round(breakPoint)}-${Math.round(nextBreak - 1)}`;
                
                return (
                  <div key={index} className="d-flex align-items-center gap-2">
                    <div
                      style={{
                        width: "20px",
                        height: "16px",
                        backgroundColor: color,
                        border: "1px solid #ddd",
                        borderRadius: "2px"
                      }}
                    />
                    <small className="text-muted">{label}</small>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="card-footer">
          <div className="row">
            <div className="col-md-6">
              <small className="text-muted">
                <i className="fas fa-mouse-pointer me-1" aria-hidden="true"></i>
                Hover states for details • Click to drill down
              </small>
            </div>
            <div className="col-md-6 text-end">
              <small className="text-muted">
                <i className="fas fa-chart-bar me-1" aria-hidden="true"></i>
                Total {userType === 'all' ? 'users' : userType}: {data.reduce((sum, state) => {
                  switch (userType) {
                    case 'clients': return sum + state.client_count;
                    case 'professionals': return sum + state.professional_count;
                    case 'all':
                    default: return sum + state.user_count;
                  }
                }, 0).toLocaleString()}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleMapDataLayer;
export { GoogleMapDataLayer };
export type { GoogleMapDataLayerProps };